/** GPX parsing (SPEC §8.1 step 1): trk/trkseg/trkpt or rte/rtept, plus named waypoints. */

export interface GpxPoint {
  lat: number;
  lon: number;
  ele?: number;
}

export interface GpxWaypoint {
  name: string;
  lat: number;
  lon: number;
}

export interface ParsedGpx {
  name: string;
  points: GpxPoint[];
  waypoints: GpxWaypoint[];
}

function childText(el: Element, local: string): string | undefined {
  for (const c of Array.from(el.children))
    if (c.localName === local) return c.textContent?.trim() ?? undefined;
  return undefined;
}

function byLocalName(root: Document | Element, local: string): Element[] {
  return Array.from(root.getElementsByTagNameNS('*', local));
}

function toPoint(el: Element): GpxPoint | null {
  const lat = Number(el.getAttribute('lat'));
  const lon = Number(el.getAttribute('lon'));
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const eleText = childText(el, 'ele');
  const ele = eleText !== undefined ? Number(eleText) : NaN;
  return Number.isFinite(ele) ? { lat, lon, ele } : { lat, lon };
}

export function parseGpx(xml: string): ParsedGpx {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new Error('Ungültige GPX-Datei (XML-Fehler)');
  if (doc.documentElement?.localName !== 'gpx') throw new Error('Keine GPX-Datei');
  let pts = byLocalName(doc, 'trkpt')
    .map(toPoint)
    .filter((p): p is GpxPoint => p !== null);
  if (pts.length < 2)
    pts = byLocalName(doc, 'rtept')
      .map(toPoint)
      .filter((p): p is GpxPoint => p !== null);
  if (pts.length < 2) throw new Error('Die GPX-Datei enthält keinen Track und keine Route');
  const waypoints: GpxWaypoint[] = byLocalName(doc, 'wpt')
    .map((el) => {
      const p = toPoint(el);
      const name = childText(el, 'name');
      return p && name ? { name, lat: p.lat, lon: p.lon } : null;
    })
    .filter((w): w is GpxWaypoint => w !== null);
  const trk = byLocalName(doc, 'trk')[0] ?? byLocalName(doc, 'rte')[0];
  const metaName = byLocalName(doc, 'metadata')[0];
  const name = (trk && childText(trk, 'name')) || (metaName && childText(metaName, 'name')) || 'Route';
  return { name, points: pts, waypoints };
}
